import React, {useState, useRef, useEffect} from "react";
import {AutoComplete, Button, Col, DatePicker, Drawer, Form, Row} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";

import InputMoney from "../../../components/billings/InputMoney";
import {messageType, showMessages} from "../../../helpers/message";
import {createPromotionalCredit, getAllPromotions} from "../../../helpers/promotion";
import {useGetBookingConfigQuery} from "../../../store/api/settingsApi";
import {dateFormat} from "../../../lib/time";

import "./PromotionalCreditAddDrawer.less";

const PromotionalCreditAddDrawer = ({addButtonText, setTableRefresh, setActiveTabFilter}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const mobileMode = window.innerWidth <= 768;
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [promotions, setPromotions] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const [selectedID, setSelectedID] = useState(0);
    const [disableEffectiveDate, setDisableEffectiveDate] = useState(true);
    const {data: bookingConfig} = useGetBookingConfigQuery();

    const onClose = () => {
        setOpen(false);
        setDisableEffectiveDate(true);
        form.resetFields();
    }

    const handleFinish = async (values) => {
        // Add the selected promotion's ID to pass in the request.
        const body = {...values, promotion_id: selectedID};
        setLoading(true);

        try {
            const abortSignal = abortSignalRef.current;
            const response = await createPromotionalCredit(body, abortSignal);
            if (response) {
                onClose();
                showMessages('Promotional Credit successfully added to account.', messageType.success);
                // Set the tab filter of the promo credit table to 'all' to refresh the total promo credit balance.
                setTableRefresh(true);
                setActiveTabFilter('all');
            }
            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
        }
    }

    const handleSearch = (value) => {
        let result = [];
        if (promotions && value) {
            // Loop through all promotions, comparing the input value to the names of the promotions.
            for (let promo of promotions) {
                if (promo.name.toLowerCase().includes(value.toLowerCase())) {
                    // If the promo name includes the typed value, add the promo data to an obj, and push that into result arr.
                    result.push({key: promo.id, label: promo.name, value: promo.name, props: promo});
                }
            }
            // The result array will become the dropdown menu, autocompleting based on the promotion name.
            setDropDownData(result);
        } else {
            // If the autocomplete field becomes empty, reset all the form fields.
            form.resetFields(['ended_at', 'started_at', 'original_amount']);
        }
    }

    const handleSelect = (value, {props}) => {
        setDisableEffectiveDate(false);

        const today = dayjs().tz();
        const startedAt = dayjs(props.started_at).tz();
        const endedAt = props.ended_at ? dayjs(props.ended_at).tz() : null;
        const defaultDuration = props.credits_default_duration;
        const defaultEndedAt = props.credits_default_ended_at ? dayjs(props.credits_default_ended_at).tz() : null;

        // BillingCutOffLength stored as days.
        const billingCutoffLength = dayjs().tz().subtract(bookingConfig.billing_cutoff_length, 'days');
        // MaxBookingFutureStart stored as seconds.
        const maxFutureStartTime = dayjs().tz().add(bookingConfig.max_booking_future_start_time, 'seconds');

        // The effective date defaults to current date.
        let effectiveDate = today;
        // Expiration date is the promo credits_default_end_date OR the date equal to default duration + current date.
        // Clone is used otherwise the original var is mutated.
        let expiryDate = defaultEndedAt ? defaultEndedAt : today.clone().add(defaultDuration, 'days');

        // If the promo is no longer (or not yet) eligible to be back/forward-dated, disable the effectiveDate field.
        if (endedAt && (endedAt.isBefore(billingCutoffLength))) {
            // If the promotion start is past the billing_cutoff_length (30 days), the promo can not invoke any more PCs.
            setDisableEffectiveDate(true);
            // Populating the effectiveDate with the end of the promo to show when the last possible day was to invoke the promo.
            effectiveDate = endedAt;
            expiryDate = defaultEndedAt ? defaultEndedAt : endedAt.clone().add(defaultDuration, 'days');
        } else if (startedAt.isAfter(maxFutureStartTime)) {
            // If the promotion doesn't start for over a year from today, it is not yet eligible to be invoked.
            setDisableEffectiveDate(true);
            effectiveDate = startedAt;
            expiryDate = defaultEndedAt ? defaultEndedAt : startedAt.clone().add(defaultDuration, 'days');
        }

        form.setFieldValue('started_at', effectiveDate);
        form.setFieldValue('ended_at', expiryDate);
        form.setFieldValue('original_amount', props.default_amount);
        setSelectedID(props.id);
    }

    // Handle mount and update.
    useEffect(() => {
        // Fetches all promotions for autocomplete filter.
        const fetchPromotions = async () => {
            setLoading(true);
            try {
                const abortSignal = abortSignalRef.current;
                const response = await getAllPromotions(1, 9999, 'all', abortSignal);
                if (response && abortSignal.aborted === false) {
                    setPromotions(response.data);
                }
                setLoading(false);
            } catch (e) {
                showMessages(e.message, messageType.error);
                setLoading(false);
                if(e.status_code === 404) window.location.replace('/404');
            }
        }
        fetchPromotions();
    }, [setTableRefresh]);

    return (
        <>
            <Button
                className="promotional-credit-add-btn"
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => setOpen(true)}
            >
                {addButtonText}
            </Button>
            <Drawer
                title="Add Promotional Credit"
                className="drawer-wrapper"
                open={open}
                onClose={onClose}
                width={mobileMode ? "100%" : "45%"}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="add-promotional-credit"
                    onFinish={handleFinish}
                >
                    <Row className="full-width-row">
                        <Form.Item
                            name="name"
                            label="Name: "
                            rules={[{
                                required: true,
                                message: 'Please enter a name.'
                            }]}
                        >
                            <AutoComplete
                                autoFocus={true}
                                placeholder="Type here"
                                options={dropDownData}
                                filterOption={true}
                                onSearch={handleSearch}
                                onSelect={handleSelect}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="started_at"
                            label="Effective Date: "
                            rules={[{
                                required: true,
                                message: 'An effective start date is required.'
                            }]}
                        >
                            <DatePicker
                                format={dateFormat}
                                placeholder="DD-MM-YYYY"
                                className="inherit-parent-width"
                                disabled={disableEffectiveDate}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="ended_at"
                            label="Expiry Date: "
                            className="inherit-parent-width"
                        >
                            <DatePicker
                                format={dateFormat}
                                placeholder="DD-MM-YYYY"
                                className="inherit-parent-width"
                                disabled={true}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="original_amount"
                            label="Promotional Credit Value: "
                        >
                            <InputMoney
                                disabled={true}
                                placeholder="0.00"
                            />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Item>
                                <Button
                                    className="cancel-button"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button loading={loading} type="primary" htmlType="submit">Add Promotional Credit</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </>
    )
}

export default PromotionalCreditAddDrawer;
